import { createThunkSlice } from 'src/redux/ReduxUtils';
import { RootState } from 'src/redux/rootReducerType';
import RentersDashboardService from 'src/services/RentersDashboard.service';
import parseQueryParams from 'src/utils/query';
import { enqueueSnackbar } from 'notistack';
import axios from 'src/utils/axios';
import {
  ComplianceUser,
  RentersDashboardCertification,
} from 'src/types/Certification';
import CertificationService from 'src/services/Certification.service';
import { RentersDashboardCategory } from 'src/types/Categories';
import {
  applyNewColumnsState,
  createGridConfig,
  getGridConfig,
  updateGridConfig,
  ValidatedGrid,
} from 'src/services/AGGridConfiguration.service';
import { AGGridColumn, AGGridConfig } from 'src/types/AGGridConfig';
import { RentersDashboardColDefs } from 'src/components/RentersDashboard/ApplicantsTable/ApplicantsTableDefinitions';
import { ColDef, GridApi } from 'ag-grid-community';
import { PayloadAction } from '@reduxjs/toolkit';
import { BulkDates } from 'src/services/TeamRentersBulkActions.service';

interface InitialState {
  isLoadingCertifications: boolean;
  isBulkCommunications: boolean;
  certifications: RentersDashboardCertification[];
  selectedCertifications: RentersDashboardCertification[];
  showOnlySelectedCertifications: boolean;
  certificationsError: string;
  categories: { [key: number | string]: RentersDashboardCategory[] };
  colDefs: null | ColDef[];
  gridConfig: null | AGGridConfig;
  gridApi: GridApi | null;
  displayedRowCount: number | null;
  fileOwnersPerProperty: Record<number, ComplianceUser[]>;
  isLoadingFileOwners: boolean;
}

const initialState: InitialState = {
  isLoadingCertifications: false,
  isBulkCommunications: false,
  certifications: [],
  selectedCertifications: [],
  showOnlySelectedCertifications: false,
  certificationsError: '',
  categories: {},
  colDefs: null,
  gridConfig: null,
  gridApi: null,
  displayedRowCount: 0,
  fileOwnersPerProperty: {},
  isLoadingFileOwners: false,
};

const RentersDashboardSlice = createThunkSlice({
  name: 'RentersDashboard',
  initialState,
  reducers: (create) => {
    return {
      fetchRentersCertifications: create.asyncThunk(
        async (params: string, { dispatch }) => {
          const response =
            await RentersDashboardService.fetchRentersCertifications(params);

          let certCategoryIds: number[] = (response.data || []).map(
            (cert: RentersDashboardCertification) => cert.category_id
          );

          certCategoryIds = Array.from(new Set(certCategoryIds));

          if (certCategoryIds.length > 0) {
            await dispatch(fetchCertificationsCategories(certCategoryIds));
          }

          // Fetch file owners for all properties in the background
          const uniquePropertyIds = [
            ...new Set((response.data || []).map((cert) => cert.property_id)),
          ];

          if (uniquePropertyIds.length > 0) {
            dispatch(fetchFileOwnersPerProperty(uniquePropertyIds));
          }

          return response.data;
        },
        {
          options: {
            condition(arg, thunkApi) {
              const { rentersDashboard } = thunkApi.getState() as RootState;
              if (rentersDashboard.isLoadingCertifications === true) {
                return false;
              }
            },
          },
          pending: (state) => {
            state.isLoadingCertifications = true;
            state.certificationsError = '';
          },
          fulfilled: (state, action) => {
            state.isLoadingCertifications = false;
            state.certifications = action.payload;
          },
          rejected: (state, action) => {
            state.isLoadingCertifications = false;
            state.certifications = [];
            state.certificationsError = action.error.message ?? 'Unknown Error';
          },
        }
      ),
      fetchDashboardGrid: create.asyncThunk(
        async () => {
          const response = await getGridConfig('renters_dashboard_config');

          if (response.data.length > 0) {
            let gridConfig = response.data[0];

            const gridColumns = getColDefs(gridConfig);
            const payload = checkGridValidity(gridColumns, gridConfig);

            if (
              payload.modifiedColumns.length > 0 ||
              payload.deletedColumns.length > 0 ||
              payload.newColumns.length > 0
            ) {
              const newResponse = await applyNewColumnsState(
                gridConfig.id,
                payload
              );
              gridConfig = newResponse.data;
            }

            return gridConfig;
          }

          const payload = {
            name: 'renters_dashboard_config',
            columns: RentersDashboardColDefs.map(
              (column: ColDef, index: number) => ({
                name: column.headerName,
                field: column.field,
                order: index,
                pinned: column.pinned,
                visibility: !column.hide,
                width: column.minWidth || 120,
              })
            ),
          };
          const newConfig = await createGridConfig(payload);
          return newConfig.data || null;
        },
        {
          // Keep the last known grid config while the refetch is in flight.
          // Nulling it here made any column save (pin / resize / reorder) that
          // landed during the request fail with 'Column not found'.
          fulfilled: (state, action) => {
            const gridConfig = action.payload;
            state.gridConfig = gridConfig;

            const sortedColDefs = getColDefs(gridConfig).sort(
              sortGridColumn(gridConfig)
            ) as unknown[];

            state.colDefs = sortedColDefs;
          },
          rejected: (state) => {
            state.gridConfig = null;
          },
        }
      ),
      fetchFileOwnersPerProperty: create.asyncThunk(
        async (propertyIds: number[]) => {
          const response = await axios.post(
            'user_profile/multiple_property_users/',
            { property_ids: propertyIds },
            { params: { fields: 'id,first_name,last_name,email' } }
          );
          return response.data;
        },
        {
          options: {
            condition(arg, thunkApi) {
              const { rentersDashboard } = thunkApi.getState() as RootState;
              if (rentersDashboard.isLoadingFileOwners === true) {
                return false;
              }
            },
          },
          pending: (state) => {
            state.isLoadingFileOwners = true;
            state.fileOwnersPerProperty = {};
          },
          fulfilled: (state, action) => {
            state.isLoadingFileOwners = false;
            state.fileOwnersPerProperty = action.payload;
          },
          rejected: (state) => {
            state.isLoadingFileOwners = false;
            state.fileOwnersPerProperty = {};
          },
        }
      ),
      fetchCertificationsCategories: create.asyncThunk(
        async (ids: number[]) => {
          const BATCH_SIZE = 500;
          const CONCURRENCY = 5;
          const params = parseQueryParams({
            expand: ['statuses'],
            fields: [
              'id',
              'statuses.id',
              'statuses.is_approved',
              'statuses.is_terminal',
              'statuses.name',
            ],
          });

          const batches: number[][] = [];
          for (let i = 0; i < ids.length; i += BATCH_SIZE) {
            batches.push(ids.slice(i, i + BATCH_SIZE));
          }

          const allData: unknown[] = [];
          for (let i = 0; i < batches.length; i += CONCURRENCY) {
            const chunk = batches.slice(i, i + CONCURRENCY);
            const results = await Promise.allSettled(
              chunk.map((batch) =>
                CertificationService.fetchCategoriesInCerts(batch, params)
              )
            );
            results
              .filter(
                (
                  r
                ): r is PromiseFulfilledResult<
                  Awaited<
                    ReturnType<
                      typeof CertificationService.fetchCategoriesInCerts
                    >
                  >
                > => r.status === 'fulfilled'
              )
              .forEach((r) => allData.push(...r.value.data));
          }

          return allData;
        },
        {
          fulfilled: (state, action) => {
            state.categories = action.payload.reduce(
              (accumulator, category) => {
                accumulator[category.id] = category.statuses;
                return accumulator;
              },
              {} as Record<number, RentersDashboardCategory[]>
            );
          },
          rejected: (state) => {
            state.categories = {};
          },
        }
      ),
      updateComplianceUser: create.asyncThunk(
        async ({
          certificationId,
          payload,
          params,
        }: ComplianceUpdateParams) => {
          const response = await CertificationService.updateCertification(
            certificationId,
            payload,
            params
          );
          return {
            certificationId,
            complianceUser: response.data.compliance_user,
          };
        },
        {
          fulfilled: (state, action) => {
            const { certificationId, complianceUser } = action.payload;
            // Look up the user across all property owner lists (replaces team-based complianceUsers)
            const allUsers = Object.values(state.fileOwnersPerProperty).flat();
            const user = allUsers.find((u) => u.id === complianceUser);
            const certificationIndex = state.certifications.findIndex(
              (cert) => cert.id === certificationId
            );

            if (certificationIndex !== -1) {
              state.certifications[certificationIndex] = {
                ...state.certifications[certificationIndex],
                compliance_user_id: complianceUser,
                compliance_user_name: user
                  ? `${user.first_name} ${user.last_name}`
                  : '',
              };
            }

            enqueueSnackbar('File Owner Updated', { variant: 'success' });
          },
          rejected: () => {
            enqueueSnackbar('An error happened updating the File Owner', {
              variant: 'error',
            });
          },
        }
      ),
      updateCertCategoryStatus: create.asyncThunk(
        async ({ payload, params = null }: UpdateCertCategoryStatusParams) => {
          try {
            const response =
              await CertificationService.updateCertCategoryStatus(
                payload,
                params
              );
            return response.data;
          } catch (error) {
            enqueueSnackbar({
              message: 'Error Updating Category Status',
              variant: 'error',
            });
            throw error;
          }
        },
        {
          fulfilled: (state, action) => {
            const { certification, status } = action.payload;
            const certIndex = state.certifications.findIndex(
              (cert) => cert.id === certification
            );
            const cert = state.certifications[certIndex];
            cert.is_terminal = status.is_terminal;
            cert.is_approved = status.is_approved;
            cert.status_name = status.name;
            cert.status_id = status.id;
            enqueueSnackbar('Certification Status Updated', {
              variant: 'success',
            });
          },
          rejected: () => {
            enqueueSnackbar(
              'An error happened updating the Certification Status',
              {
                variant: 'error',
              }
            );
          },
        }
      ),
      updateCertDeadlineDate: create.asyncThunk(
        async ({ payload }: UpdateCertDeadlineDatePayload) => {
          try {
            const response = await CertificationService.updateCertification(
              payload.certification,
              { deadline_date: payload.deadline_date }
            );
            return response.data;
          } catch (error) {
            enqueueSnackbar({
              message: 'Error updating deadline date',
              variant: 'error',
            });
            throw error;
          }
        },
        {
          fulfilled: (state, action) => {
            const { id, deadline_date } = action.payload;
            const certIndex = state.certifications.findIndex(
              (cert) => cert.id === id
            );
            const cert = state.certifications[certIndex];
            cert.deadline_date = deadline_date;
            enqueueSnackbar('Certification Deadline Date Updated', {
              variant: 'success',
            });
          },
          rejected: () => {
            enqueueSnackbar(
              'An error happened updating the Certification Deadline Date',
              {
                variant: 'error',
              }
            );
          },
        }
      ),
      reOrderColumns: create.asyncThunk(
        async ({ newIndex, colId }: reOrderColumnsParams, { getState }) => {
          const { rentersDashboard } = getState() as RootState;
          const columns = rentersDashboard.gridConfig?.columns || [];
          const oldIndex = columns.findIndex(
            (column) => column.field === colId
          );

          // `oldIndex === -1` means the column is not in the saved layout (for
          // example the grid config is still loading). Skip silently instead of
          // failing the thunk and surfacing a false "error updating the column
          // order" toast. We must NOT early-return on newIndex === -1: bulk
          // communications intentionally passes newIndex - 1 for the first slot.
          if (oldIndex === -1) {
            return { noUpdate: true };
          }

          const newColumns: AGGridColumn[] = JSON.parse(
            JSON.stringify(columns)
          );
          const removedColumn = newColumns.splice(oldIndex, 1)[0];
          newColumns.splice(newIndex, 0, removedColumn);
          newColumns.forEach((column, index) => {
            column.order = index;
          });
          const modifiedColumns = newColumns.slice(
            Math.min(newIndex, oldIndex),
            Math.max(newIndex, oldIndex) + 1
          );

          const result = await updateGridConfig(
            rentersDashboard.gridConfig.id,
            { columns: modifiedColumns }
          );

          return result.data;
        },
        {
          fulfilled: (state, action) => {
            if (action.payload.noUpdate) {
              return;
            }
            const gridConfig = action.payload;

            state.gridConfig = gridConfig;
            state.colDefs = state.colDefs.sort(sortGridColumn(gridConfig));

            enqueueSnackbar('Columns ordered saved!', { variant: 'success' });
          },
          rejected: () => {
            enqueueSnackbar('An error happened updating the column order', {
              variant: 'error',
            });
          },
        }
      ),
      updateColumn: create.asyncThunk(
        async ({ colId, values }: UpdateColumnParams, { getState }) => {
          const { rentersDashboard } = getState() as RootState;
          const columns = rentersDashboard.gridConfig?.columns || [];
          const columnIndex = columns.findIndex((col) => col.field === colId);

          if (columnIndex === -1) {
            // The column is not in the saved layout (for example the grid config
            // is still loading). Skip silently instead of failing the thunk and
            // surfacing a false "error updating the column" toast.
            return { noUpdate: true };
          }
          const needsUpdate = Object.keys(values).some(
            (key) => columns[columnIndex][key] !== values[key]
          );
          if (!needsUpdate) {
            return { noUpdate: true };
          }

          const result = await updateGridConfig(
            rentersDashboard.gridConfig.id,
            { columns: [{ ...columns[columnIndex], ...values }] }
          );

          return result.data;
        },
        {
          fulfilled: (state, action) => {
            if (action.payload.noUpdate) {
              return;
            }
            state.gridConfig = action.payload;
            enqueueSnackbar('Column successfully updated!', {
              variant: 'success',
            });
          },
          rejected: () => {
            enqueueSnackbar('An error happened updating the column', {
              variant: 'error',
            });
          },
        }
      ),
      setSelectedCertifications: create.reducer(
        (state, action: PayloadAction<RentersDashboardCertification[]>) => {
          state.selectedCertifications = action.payload;
        }
      ),
      clearSelectedCertifications: create.reducer((state) => {
        state.selectedCertifications = [];
      }),
      removeSelectedCertification: create.reducer(
        (state, action: PayloadAction<number>) => {
          state.selectedCertifications = state.selectedCertifications.filter(
            (cert) => cert.id !== action.payload
          );
        }
      ),
      toggleShowSelectedCertifications: create.reducer((state) => {
        state.showOnlySelectedCertifications =
          !state.showOnlySelectedCertifications;
      }),
      setIsBulkCommunications: create.reducer(
        (state, action: PayloadAction<boolean>) => {
          state.isBulkCommunications = action.payload;
        }
      ),
      setDisplayedRowCount: create.reducer(
        (state, action: PayloadAction<number>) => {
          state.displayedRowCount = action.payload;
        }
      ),
      applyBulkStatusUpdates: create.reducer(
        (
          state,
          action: PayloadAction<
            Array<{ certificationId: number; statusId: number }>
          >
        ) => {
          const updatesById = new Map(
            action.payload.map((u) => [u.certificationId, u.statusId])
          );

          const updateCert = (
            cert: RentersDashboardCertification
          ): RentersDashboardCertification => {
            const newStatusId = updatesById.get(cert.id);
            if (!newStatusId) {
              return cert;
            }
            const statuses = state.categories[cert.category_id] || [];
            const statusObj = statuses.find((s) => s.id === newStatusId);
            if (!statusObj) {
              return cert;
            }
            return {
              ...cert,
              status_id: statusObj.id,
              status_name: statusObj.name,
              is_terminal: !!statusObj.is_terminal,
              is_approved: !!statusObj.is_approved,
            } as RentersDashboardCertification;
          };

          state.certifications = state.certifications.map(updateCert);
          state.selectedCertifications =
            state.selectedCertifications.map(updateCert);
        }
      ),
      applyBulkFileOwnerUpdates: create.reducer(
        (
          state,
          action: PayloadAction<
            Array<{ certificationId: number; fileOwnerId: number | null }>
          >
        ) => {
          const updatesById = new Map(
            action.payload.map((u) => [u.certificationId, u.fileOwnerId])
          );

          const updateCert = (
            cert: RentersDashboardCertification
          ): RentersDashboardCertification => {
            const newFileOwnerId = updatesById.get(cert.id);
            if (newFileOwnerId === undefined) {
              return cert;
            }

            // Find the compliance user in the fileOwnersPerProperty for this certification's property
            const propertyId = cert.property_id;
            const propertyFileOwners =
              state.fileOwnersPerProperty[propertyId] || [];
            const complianceUser = propertyFileOwners.find(
              (user) => user.id === newFileOwnerId
            );

            return {
              ...cert,
              compliance_user_id: newFileOwnerId,
              compliance_user_name: complianceUser
                ? `${complianceUser.first_name} ${complianceUser.last_name}`
                : null,
            } as RentersDashboardCertification;
          };

          state.certifications = state.certifications.map(updateCert);
          state.selectedCertifications =
            state.selectedCertifications.map(updateCert);
        }
      ),
      applyBulkDatesUpdates: create.reducer(
        (
          state,
          action: PayloadAction<
            Array<{
              certificationId: number;
              dates: BulkDates;
            }>
          >
        ) => {
          const updatesById = new Map(
            action.payload.map((u) => [u.certificationId, u.dates])
          );

          const updateCert = (
            cert: RentersDashboardCertification
          ): RentersDashboardCertification => {
            const dateUpdates = updatesById.get(cert.id);
            if (!dateUpdates) {
              return cert;
            }

            return {
              ...cert,
              ...(dateUpdates.deadline_date !== undefined && {
                deadline_date: dateUpdates.deadline_date || '',
              }),
              ...(dateUpdates.lease_start !== undefined && {
                lease_start: dateUpdates.lease_start,
              }),
              ...(dateUpdates.lease_end !== undefined && {
                lease_end: dateUpdates.lease_end,
              }),
              ...(dateUpdates.move_in_date !== undefined && {
                move_in_date: dateUpdates.move_in_date,
              }),
              ...(dateUpdates.move_out_date !== undefined && {
                move_out_date: dateUpdates.move_out_date || '',
              }),
            } as RentersDashboardCertification;
          };

          state.certifications = state.certifications.map(updateCert);
          state.selectedCertifications =
            state.selectedCertifications.map(updateCert);
        }
      ),
      restoreGridConfig: create.reducer((state) => {
        state.gridConfig = null;
      }),
      setGridApi: create.reducer(
        (state, action: PayloadAction<GridApi | null>) => {
          state.gridApi = action.payload;
        }
      ),
      setFilterModelString: create.reducer(
        (state, action: PayloadAction<string | null>) => {
          if (state.gridConfig) {
            state.gridConfig.filter_model_string = action.payload;
          }
        }
      ),
      modifyGridColumVisibility: create.reducer(
        (state, action: PayloadAction<AGGridColumn>) => {
          const columnIndex = state.gridConfig.columns.findIndex(
            (col: AGGridColumn) => col.field === action.payload.field
          );
          if (columnIndex !== -1) {
            state.gridConfig.columns[columnIndex].visibility =
              action.payload.visibility;

            state.colDefs[columnIndex].hide = !action.payload.visibility;
          }
        }
      ),
      resetState: create.reducer(() => initialState),
    };
  },
});

export const {
  fetchRentersCertifications,
  fetchDashboardGrid,
  fetchFileOwnersPerProperty,
  fetchCertificationsCategories,
  updateComplianceUser,
  updateCertCategoryStatus,
  updateCertDeadlineDate,
  setSelectedCertifications,
  toggleShowSelectedCertifications,
  clearSelectedCertifications,
  removeSelectedCertification,
  setDisplayedRowCount,
  applyBulkStatusUpdates,
  setIsBulkCommunications,
  setGridApi,
  setFilterModelString,
  modifyGridColumVisibility,
  reOrderColumns,
  updateColumn,
  resetState,
  restoreGridConfig,
  applyBulkFileOwnerUpdates,
  applyBulkDatesUpdates,
} = RentersDashboardSlice.actions;

export default RentersDashboardSlice.reducer;

interface ComplianceUpdateParams {
  certificationId: number;
  payload: { compliance_user: number };
  params?: string;
}

interface UpdateCertCategoryStatusParams {
  payload: { certification: number; status: number };
  params?: string;
}

interface UpdateCertDeadlineDatePayload {
  payload: { certification: string; deadline_date: string };
}

interface reOrderColumnsParams {
  newIndex: number;
  colId: string;
}

interface UpdateColumnParams {
  colId: string;
  values: Partial<AGGridColumn>;
}

const getColDefs = (gridConfig): ColDef[] =>
  RentersDashboardColDefs.map((column) => {
    const configColumn = gridConfig.columns.find(
      (configColumn) => configColumn.name === column.headerName
    );

    return {
      ...column,
      hide: configColumn ? !configColumn.visibility : column.hide,
      pinned: configColumn ? configColumn.pinned : column.pinned,
      width: configColumn ? configColumn.width : column.width,
    };
  });

const findModifiedColumn = (columns: ColDef[], col: AGGridColumn): ColDef => {
  return columns.find(
    (c) =>
      (c.field === col.field && c.headerName !== col.name) ||
      (c.field !== col.field && c.headerName === col.name)
  );
};

const checkGridValidity = (
  columns: ColDef[],
  gridConfig: AGGridConfig
): ValidatedGrid => {
  const modifiedColumns = gridConfig.columns
    .filter((col) => findModifiedColumn(columns, col))
    .map((col) => {
      const newCol = findModifiedColumn(columns, col);
      return { ...col, name: newCol.headerName, field: newCol.field };
    });

  const deletedColumns = gridConfig.columns.filter(
    (col) =>
      !columns.find((c) => c.field === col.field || c.headerName === col.name)
  );

  const newColumns = columns
    .filter(
      (col) =>
        !gridConfig.columns.find(
          (c) => c.field === col.field || col.headerName === c.name
        )
    )
    .map((col, i) => {
      // Append new columns after all existing stored columns to avoid order
      // collisions with columns that are already persisted in the grid config.
      return { ...col, order: gridConfig.columns.length + i };
    });

  return { modifiedColumns, deletedColumns, newColumns };
};

const sortGridColumn =
  (gridConfig) =>
  (a, b): number => {
    const aConfig = gridConfig.columns.find((col) => col.field === a.field);
    const bConfig = gridConfig.columns.find(
      (configColumn) => configColumn.field === b.field
    );
    return aConfig.order - bConfig.order;
  };
